/**
 * ProductExperiencePanel - Unified product detail view with My Picks
 * 
 * Features:
 * - Slide-in panel (desktop) / Bottom sheet (mobile)
 * - Product details with image, price, description
 * - Quick actions: Add to Cart, Wishlist toggle
 * - AI Match reasoning (why this product fits your profile)
 * - My Picks section showing saved wishlist items
 * 
 * Business Impact:
 * - Keeps users in-app instead of external navigation
 * - Increases conversion by showing relevant product info
 * - Cross-sell opportunity via My Picks section
 * 
 * @author Heseda AI Team
 * @version 1.0
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { WebSearchResultItem } from '../../types/conversation';
import type { CartItem, WishlistItem } from '../../types/product';
import { isInStoreProduct } from '../../types/product';
import type { WishlistSource } from '../../hooks/useWishlist';
import { COMPACT_BP } from '../../hooks/useResponsive';
import { INHOUSE_SELLING_ENABLED } from '../../config/adminConfig';
import { cleanProductTitle } from '../../utils/productTitle';
import { openProductUrl } from '../../utils/openProductUrl';
import './ProductExperiencePanel.css';
import { trackProductInteraction } from '../../services/activityTrackingService';

/** Tab options for the panel */
type PanelTab = 'details' | 'my-picks';

export interface ProductExperiencePanelProps {
  /** Optional id to link toggle buttons via aria-controls */
  panelId?: string;
  /** Whether the panel is open */
  isOpen: boolean;
  /** Callback to close the panel */
  onClose: () => void;
  /** Product to display (from search result) */
  product: WebSearchResultItem | null;
  /** UI language */
  language?: string;
  /** Add to cart callback */
  onAddToCart?: (item: CartItem) => void;
  /** Toggle wishlist callback */
  onWishlistToggle?: (item: WishlistItem, source?: WishlistSource) => void;
  /** Direct remove from wishlist by ID (bypasses toggle logic) */
  onRemoveFromWishlist?: (productId: string) => void;
  /** Check if product is in wishlist */
  isInWishlist?: (productId: string) => boolean;
  /** Wishlist items for My Picks section */
  wishlistItems?: WishlistItem[];
  /** Whether the viewport is compact (mobile) */
  isCompact?: boolean;
}

/**
 * ProductExperiencePanel - Unified product experience
 */
export default function ProductExperiencePanel({
  panelId,
  isOpen,
  onClose,
  product,
  language = 'ko',
  onAddToCart,
  onWishlistToggle,
  onRemoveFromWishlist,
  isInWishlist,
  wishlistItems = [],
  isCompact: isCompactProp,
}: ProductExperiencePanelProps): React.ReactElement | null {
  
  const [activeTab, setActiveTab] = useState<PanelTab>('details');
  const [isCompact, setIsCompact] = useState(isCompactProp ?? window.innerWidth < COMPACT_BP);
  // For viewing a product from wishlist (My Picks)
  const [viewingProduct, setViewingProduct] = useState<WebSearchResultItem | null>(null);

  // Translation hook
  const { t } = useTranslation('common');

  // Track window resize for responsive behavior
  useEffect(() => {
    if (isCompactProp !== undefined) {
      setIsCompact(isCompactProp);
      return;
    }
    
    const handleResize = () => setIsCompact(window.innerWidth < COMPACT_BP);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isCompactProp]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.documentElement.classList.add('body--panel-open');
    } else {
      document.documentElement.classList.remove('body--panel-open');
    }
    return () => document.documentElement.classList.remove('body--panel-open');
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Set tab based on whether product is provided
  // No product = show My Picks, product = show details
  // Also reset viewingProduct when panel opens fresh
  useEffect(() => {
    if (isOpen) {
      setActiveTab(product ? 'details' : 'my-picks');
      setViewingProduct(null); // Reset when panel opens
    }
  }, [isOpen, product?.id, product?.link, product]);

  // Swipe-to-remove state for My Picks cards (hooks must be before early return)
  const touchStartRef = useRef<{ x: number; y: number; id: string } | null>(null);
  const [swipingId, setSwipingId] = useState<string | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);

  if (!isOpen) return null;

  // Use displayProduct which is defined after handlePickClick
  // We'll compute it here for the variables we need before handlePickClick is defined
  const currentProduct = viewingProduct || product;
  const productId = currentProduct?.id || currentProduct?.link || '';
  const inWishlist = isInWishlist?.(productId) ?? false;
  const priceValue = typeof currentProduct?.price === 'number' 
    ? currentProduct.price 
    : parseFloat(String(currentProduct?.price)) || 0;

  /**
   * Handle Add to Cart
   */
  const handleAddToCart = () => {
    if (!currentProduct || !onAddToCart) return;
    
    // User activity
    trackProductInteraction(currentProduct.id || "N/A", currentProduct.name || "N/A", currentProduct.source || 'web', 'add-to-cart');

    onAddToCart({
      id: productId,
      name: currentProduct.name || 'Product',
      priceUSD: priceValue,
      image: currentProduct.image || '',
      url: currentProduct.link,
      quantity: 1,
      source: currentProduct.source || 'web',
    });
  };

  /**
   * Handle Wishlist Toggle
   */
  const handleWishlistToggle = () => {
    if (!currentProduct || !onWishlistToggle) return;
    
    const wishlistItem: WishlistItem = {
      id: productId,
      name: currentProduct.name || 'Product',
      priceUSD: priceValue,
      image: currentProduct.image || '',
      url: currentProduct.link,
      source: currentProduct.source,
    };
    onWishlistToggle(wishlistItem, 'product-page');
  };

  /**
   * Handle wishlist item click (from My Picks)
   * - In-store products (Heseda - internal URLs): Show product details in panel
   * - Web products (external URLs): Open external link
   * 
   * Uses URL pattern detection for robustness (works even if source field is missing)
   */
  const handlePickClick = (item: WishlistItem) => {
    // Determine if product is in-store by URL pattern (more reliable than source field)
    // Heseda products have URLs like /catalogs/products/SKU
    const isInStore = isInStoreProduct(item.url) || item.source === 'inhouse';
    
    if (isInStore) {
      // In-store products - show details in panel
      setViewingProduct({
        link: item.url,
        name: item.name,
        image: item.image,
        price: item.priceUSD,
        source: (item.source === 'inhouse' || item.source === 'partner' || item.source === 'whitelist' || item.source === 'web') ? item.source : 'inhouse',
        displayLink: item.brand,
        id: item.id,
      });
      setActiveTab('details');
    } else {
      // Web products - open in native shopping app or browser
      openProductUrl(item.url);
    }
  };

  // Product to display - either passed prop or selected from wishlist
  const displayProduct = viewingProduct || product;

  /**
   * Remove item from wishlist (from My Picks)
   * Uses direct removal (not toggle) to prevent stale-closure re-adds
   */
  const handleRemovePick = (item: WishlistItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemoveFromWishlist) {
      onRemoveFromWishlist(item.id);
    } else {
      onWishlistToggle?.(item, 'product-page');
    }
  };

  // Swipe-to-remove touch handlers for My Picks cards
  const handleTouchStart = (item: WishlistItem, e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, id: item.id };
    setSwipingId(null);
    setSwipeOffset(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;
    // Only horizontal swipe (ignore vertical scroll)
    if (Math.abs(dx) > Math.abs(dy) && dx < 0) {
      setSwipingId(touchStartRef.current.id);
      setSwipeOffset(Math.max(dx, -120));
    }
  };

  const handleTouchEnd = (item: WishlistItem) => {
    if (swipeOffset < -80) {
      // Swiped far enough — remove
      if (onRemoveFromWishlist) {
        onRemoveFromWishlist(item.id);
      } else {
        onWishlistToggle?.(item, 'product-page');
      }
    }
    touchStartRef.current = null;
    setSwipingId(null);
    setSwipeOffset(0);
  };

  // Panel content
  const panelContent = (
    <div className="product-panel__content">
      {/* Tab Navigation */}
      <nav className="product-panel__tabs" role="tablist">
        {/* Only show Product tab when there's an active product to display */}
        {currentProduct && (
        <button
          role="tab"
          aria-selected={activeTab === 'details'}
          className={`product-panel__tab ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          {currentProduct ? (isInStoreProduct(currentProduct.link) || currentProduct.source === 'inhouse' ? '⭐' : '📦') : '📦'} {t('product.tab')}
        </button>
        )}
        {(INHOUSE_SELLING_ENABLED || wishlistItems.length > 0 || activeTab === 'my-picks') && (
        <button
          role="tab"
          aria-selected={activeTab === 'my-picks'}
          className={`product-panel__tab ${activeTab === 'my-picks' ? 'active' : ''}`}
          onClick={() => setActiveTab('my-picks')}
        >
          ❤️ {t('product.myPicks')} {wishlistItems.length > 0 && `(${wishlistItems.length})`}
        </button>
        )}
      </nav>

      {/* Tab Content */}
      <div className="product-panel__tab-content">
        {/* Details Tab */}
        {activeTab === 'details' && currentProduct && (
          <div className="product-panel__details">
            {/* Product Image */}
            {currentProduct.image && (
              <div className="product-panel__image-container">
                <img 
                  src={currentProduct.image} 
                  alt={currentProduct.name || 'Product'} 
                  className="product-panel__image"
                  onError={(e) => { (e.target as HTMLImageElement).src = '/images/placeholder-product.svg'; }}
                />
                {/* Heseda badge - show for internal catalog products */}
                {(isInStoreProduct(currentProduct.link) || currentProduct.source === 'inhouse') && (
                  <span className="product-panel__badge product-panel__badge--inhouse">
                    ⭐ Heseda
                  </span>
                )}
              </div>
            )}

            {/* Product Info */}
            <div className="product-panel__info">
              <h2 className="product-panel__title">{cleanProductTitle(currentProduct.name || 'Product')}</h2>
              
              {currentProduct.displayLink && (
                <p className="product-panel__brand">{currentProduct.displayLink}</p>
              )}
              
              {currentProduct.price && (
                <p className="product-panel__price">
                  {currentProduct.currency || '$'}{priceValue.toFixed(2)}
                </p>
              )}

              {/* AI Match Reason */}
              {currentProduct.reason && (
                <div className="product-panel__ai-match">
                  <span className="product-panel__ai-icon">🎯</span>
                  <div>
                    <strong>{t('product.aiMatch')}</strong>
                    <p>{currentProduct.reason}</p>
                  </div>
                </div>
              )}

              {/* Description */}
              {currentProduct.snippet && (
                <p className="product-panel__description">{currentProduct.snippet}</p>
              )}
            </div>

            {/* Action Buttons - only when selling features enabled */}
            {INHOUSE_SELLING_ENABLED && (
            <div className="product-panel__actions">
              <button
                className="product-panel__btn product-panel__btn--cart"
                onClick={handleAddToCart}
                disabled={!currentProduct.price}
              >
                🛒 {t('product.addToCart')}
              </button>
              
              <button
                className={`product-panel__btn product-panel__btn--wishlist ${inWishlist ? 'active' : ''}`}
                onClick={handleWishlistToggle}
              >
                {inWishlist ? '❤️' : '♡'} {inWishlist ? t('product.saved') : t('product.save')}
              </button>
            </div>
            )}

            {/* External Link - Only show for web/external products, not in-store (Heseda) */}
            {!isInStoreProduct(currentProduct.link) && currentProduct.source !== 'inhouse' && currentProduct.source !== 'partner' && (
              <a
                href={currentProduct.link}
                target="_blank"
                rel="noopener noreferrer"
                className="product-panel__external-link"
              >
                {t('product.viewExternal')} →
              </a>
            )}
          </div>
        )}

        {/* My Picks Tab */}
        {activeTab === 'my-picks' && (
          <div className="product-panel__my-picks">
            {wishlistItems.length === 0 ? (
              <div className="product-panel__empty">
                <span className="product-panel__empty-icon">♡</span>
                <p>{t('product.noSavedItems')}</p>
                <p className="product-panel__empty-hint">
                  {t('product.emptyHint')}
                </p>
              </div>
            ) : (
              <div className="product-panel__picks-grid">
                {wishlistItems.map((item) => {
                  const displayName = cleanProductTitle(item.name) || item.brand || t('picks.untitledProduct', 'Skincare product');
                  const hasCleanTitle = Boolean(cleanProductTitle(item.name));
                  return (
                  <div 
                    key={item.id} 
                    className={`product-panel__pick-card ${swipingId === item.id ? 'swiping' : ''}`}
                    onClick={() => handlePickClick(item)}
                    onTouchStart={(e) => handleTouchStart(item, e)}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={() => handleTouchEnd(item)}
                    style={swipingId === item.id ? { transform: `translateX(${swipeOffset}px)`, transition: 'none' } : undefined}
                  >
                    {item.image && (
                      <div className="product-panel__pick-image">
                        <img
                          src={item.image}
                          alt={displayName}
                          onError={(e) => { (e.target as HTMLImageElement).src = '/images/placeholder-product.svg'; }}
                        />
                      </div>
                    )}
                    <div className="product-panel__pick-info">
                      <h4 className="product-panel__pick-title">{displayName}</h4>
                      {item.brand && hasCleanTitle && (
                        <p className="product-panel__pick-brand">{item.brand}</p>
                      )}
                      <p className="product-panel__pick-price">
                        {item.priceUSD > 0
                          ? `$${item.priceUSD.toFixed(2)}`
                          : <span className="product-panel__pick-price--check">{t('picks.checkPrice', 'Check price →')}</span>}
                      </p>
                    </div>
                    <button
                      className="product-panel__pick-remove"
                      onClick={(e) => handleRemovePick(item, e)}
                      aria-label={t('buttons.remove')}
                    >
                      ✕
                    </button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* No Product Selected */}
        {activeTab === 'details' && !currentProduct && (
          <div className="product-panel__empty">
            <span className="product-panel__empty-icon">📦</span>
            <p>{t('product.selectProduct')}</p>
          </div>
        )}
      </div>
    </div>
  );

  // Render as portal
  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`product-panel__backdrop ${isOpen ? 'open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel - different styles for mobile vs desktop */}
      <aside
        id={panelId}
        className={`product-panel ${isCompact ? 'product-panel--mobile' : 'product-panel--desktop'} ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('product.details')}
      >
        {/* Handle for mobile bottom sheet */}
        {isCompact && <div className="product-panel__handle" />}
        
        {/* Header */}
        <header className="product-panel__header">
          <h3>{activeTab === 'details' ? t('product.details') : t('product.myPicks')}</h3>
          <button
            className="product-panel__close"
            onClick={onClose}
            aria-label={t('buttons.close')}
          >
            ✕
          </button>
        </header>

        {/* Scrollable Content */}
        <div className="product-panel__scroll">
          {panelContent}
        </div>
      </aside>
    </>,
    document.body
  );
}
